import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { rows } from "@/lib/api";
import { toast } from "sonner";
import ProductCard from "@/components/store/ProductCard";
import HeroSlider from "@/components/store/HeroSlider";
import { Scale, Thumb } from "@/components/ui-kit";
import {
  ShieldCheck, Truck, ArrowRight, MessageCircle, QrCode, FileText, Undo2, ChevronDown, Send,
  ChevronLeft, ChevronRight, Tag,
} from "lucide-react";

/**
 * Os três caminhos que a Ortopédica realmente oferece.
 *
 * Aluguel (com caução e contrato) e sob-medida (ordem de serviço) existiam só
 * no painel: quem chegava pela loja não sabia que podia alugar uma cadeira em
 * vez de comprar por R$ 8.990. O hero passa a abrir por eles.
 *
 * Comprar é o único com fluxo self-service; os outros dois começam no
 * atendimento, então levam ao WhatsApp com a mensagem já escrita.
 */
const WAYS = [
  {
    key: "comprar",
    label: "Comprar",
    lead: "Catálogo completo",
    desc: "Órteses, cadeiras, andadores e meias. Entrega para todo o Brasil ou retirada na loja.",
    cta: "Ver produtos",
    to: "/produtos",
    primary: true,
  },
  {
    key: "alugar",
    label: "Alugar",
    lead: "Para quem precisa por um tempo",
    desc: "Cadeiras de rodas, andadores e camas hospitalares, com caução e contrato.",
    cta: "Falar sobre aluguel",
    wa: "Olá! Gostaria de alugar um equipamento.",
  },
  {
    key: "sob-medida",
    label: "Sob medida",
    lead: "Quando o pronto não serve",
    desc: "Palmilhas e órteses feitas a partir das suas medidas, com prazo combinado.",
    cta: "Falar sobre sob medida",
    wa: "Olá! Preciso de uma peça sob medida.",
  },
];

const TRUST = [
  { icon: Truck, title: "Envio para todo o Brasil", desc: "Correios e Melhor Envio" },
  { icon: QrCode, title: "5% OFF no PIX", desc: "Desconto aplicado no checkout" },
  { icon: ShieldCheck, title: "Compra 100% segura", desc: "Seus dados protegidos" },
  { icon: FileText, title: "Nota fiscal sempre", desc: "NF-e em todas as compras" },
  { icon: Undo2, title: "Troca facilitada", desc: "Até 7 dias após o recebimento" },
  { icon: MessageCircle, title: "Suporte humano", desc: "WhatsApp em horário comercial" },
];

const FAQ = [
  { q: "Como escolher o tamanho correto da órtese ou bota?", a: "Cada produto tem tabela de medidas na página de detalhe. Em caso de dúvida, chame nosso time no WhatsApp com as medidas do paciente — orientamos a escolha sem custo." },
  { q: "Vocês alugam equipamentos como cadeiras de rodas?", a: "Sim! Trabalhamos com aluguel de cadeiras de rodas, andadores e camas hospitalares com caução. Entre em contato pelo WhatsApp para verificar disponibilidade." },
  { q: "Qual o prazo de entrega?", a: "O prazo aparece no checkout após informar o CEP: PAC (até 8 dias úteis), SEDEX (até 3) ou Expresso (até 5). Compras acima de R$ 399 têm frete PAC grátis." },
  { q: "Posso retirar na loja física?", a: "Sim — loja física e online compartilham o mesmo estoque. Finalize a compra e combine a retirada pelo WhatsApp." },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-medium text-slate-900 text-sm">{q}</span>
        <ChevronDown size={18} className={`text-brand-600 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}


/**
 * Carrossel dos destaques.
 *
 * Rolagem NATIVA com `scroll-snap`, e não um slider de biblioteca: no celular o
 * dedo já faz a coisa certa sem JavaScript nenhum, e a vitrine continua rolável
 * se o JS falhar. As setas e os pontos são camada de conforto por cima disso —
 * nada aqui é a única forma de chegar ao quarto produto.
 *
 * Três decisões de interface que valem explicação:
 *
 * SETAS SOBRE A IMAGEM, e não no cabeçalho. No cabeçalho elas ficam longe do
 * conteúdo que movem, e a pessoa precisa mirar num canto da tela para andar
 * numa lista que está no centro. Ficam na altura do MEIO DA FOTO, medida em
 * tempo real: centrar no cartão inteiro colocaria a seta na altura do preço,
 * cobrindo justamente o que decide a compra.
 *
 * PONTOS embaixo. A seta diz "tem mais"; só o ponto diz QUANTO mais e ONDE eu
 * estou. Sem eles, uma vitrine de 6 e uma de 40 se parecem, e ninguém sabe se
 * vale continuar rolando. São páginas, não produtos — é o que a seta move.
 *
 * SETA SOME NA PONTA, em vez de ficar apagada. Seta desabilitada por cima de
 * uma foto é ruído sobre conteúdo: ela cobre o produto para comunicar que não
 * faz nada.
 */
function FeaturedSlider({ products }) {
  const trilho = useRef(null);
  const [pode, setPode] = useState({ antes: false, depois: false });
  const [paginas, setPaginas] = useState({ total: 1, atual: 0 });
  // Meia altura da foto, em px. Enquanto não medimos, as setas não são
  // desenhadas — melhor aparecerem no lugar certo um quadro depois do que
  // saltarem de uma posição chutada para a correta.
  const [meioDaFoto, setMeioDaFoto] = useState(null);

  const medir = () => {
    const el = trilho.current;
    if (!el) return;

    // 1px de folga: `scrollWidth` e `clientWidth` diferem por arredondamento de
    // subpixel mesmo no fim do curso, e sem a folga a seta nunca desliga.
    setPode({
      antes: el.scrollLeft > 1,
      depois: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });

    /* A página atual sai do PROGRESSO da rolagem, e não de `scrollLeft` dividido
       pela largura. A última página quase nunca é inteira — com 5 produtos e 4
       por tela, o curso todo mede um cartão — e a divisão marcaria "página 1"
       com a pessoa parada no fim. O progresso mapeia início→0 e fim→última,
       qualquer que seja a sobra. */
    const total = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
    const curso = el.scrollWidth - el.clientWidth;
    setPaginas({
      total,
      atual: curso > 0 ? Math.round((el.scrollLeft / curso) * (total - 1)) : 0,
    });

    // A foto é `aspect-square`, então a altura dela é a LARGURA do cartão —
    // dá para posicionar a seta antes de qualquer imagem terminar de carregar.
    const cartao = el.querySelector("li");
    if (cartao) setMeioDaFoto(cartao.getBoundingClientRect().width / 2);
  };

  useEffect(() => {
    medir();
    const el = trilho.current;
    if (!el) return;
    // O ResizeObserver cobre a virada de orientação do celular, que muda quantos
    // cartões cabem — e com isso a altura da foto — sem disparar rolagem nenhuma.
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [products.length]);

  /* Uma página por vez, exatamente `clientWidth`: é o que faz a seta e o ponto
     contarem a MESMA história. Com um passo de 90% eles divergiriam já no
     segundo clique, e o ponto passaria a mentir sobre onde a pessoa está. */
  const irPara = (px) => {
    const el = trilho.current;
    if (!el) return;
    const suave = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ left: px, behavior: suave ? "smooth" : "auto" });
  };
  const rolar = (dir) => trilho.current && irPara(trilho.current.scrollLeft + dir * trilho.current.clientWidth);

  const seta = "absolute z-10 hidden md:inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/95 backdrop-blur-sm text-slate-700 shadow-lg ring-1 ring-slate-900/10 hover:bg-white hover:text-brand-600 hover:scale-105 active:scale-95 disabled:opacity-0 disabled:pointer-events-none transition-all duration-200";

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-6">
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-slate-900">Destaques da semana</h2>
        <Link to="/produtos" data-testid="view-all-products-link"
          className="shrink-0 text-brand-600 font-medium text-sm hover:underline">Ver todos →</Link>
      </div>

      <div className="relative">
        <ul
          ref={trilho}
          onScroll={medir}
          data-testid="featured-products-slider"
          /* Região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
             sem isto, quem navega por Tab não alcança nem as setas nem a
             rolagem, e os produtos a partir do quinto ficam inacessíveis. A
             regra do lint não modela contêiner de rolagem — daí a exceção.
             Supressão sem nome de regra de propósito: o build usa a config do
             CRA, que não carrega o plugin jsx-a11y, e citar a regra por nome
             ali vira "rule not found" e quebra a compilação. */
          // eslint-disable-next-line
          tabIndex={0}
          aria-label="Produtos em destaque"
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {products.map((p) => (
            <li key={p.id} className="snap-start shrink-0 w-[calc(50%-0.625rem)] md:w-[calc(33.333%-0.833rem)] lg:w-[calc(25%-0.9375rem)]">
              <ProductCard product={p} />
            </li>
          ))}
        </ul>

        {meioDaFoto !== null && (
          <>
            <button type="button" onClick={() => rolar(-1)} disabled={!pode.antes}
              style={{ top: meioDaFoto }}
              aria-label="Ver destaques anteriores" data-testid="featured-prev"
              className={`${seta} left-3 -translate-y-1/2`}><ChevronLeft size={20} aria-hidden /></button>
            <button type="button" onClick={() => rolar(1)} disabled={!pode.depois}
              style={{ top: meioDaFoto }}
              aria-label="Ver mais destaques" data-testid="featured-next"
              className={`${seta} right-3 -translate-y-1/2`}><ChevronRight size={20} aria-hidden /></button>
          </>
        )}
      </div>

      {/* Um ponto só não indica nada: se tudo cabe na tela, não há posição a
          comunicar e a fileira vira enfeite. */}
      {paginas.total > 1 && (
        <div className="flex justify-center gap-2 mt-5" data-testid="featured-dots">
          {Array.from({ length: paginas.total }, (_, i) => {
            const ativo = i === paginas.atual;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { const el = trilho.current; if (el) irPara((i / Math.max(1, paginas.total - 1)) * (el.scrollWidth - el.clientWidth)); }}
                aria-label={`Ir para a página ${i + 1} de ${paginas.total} dos destaques`}
                aria-current={ativo ? "true" : undefined}
                data-testid="featured-dot"
                /* O alvo de toque tem 24px de altura mesmo com o ponto medindo
                   8px: o miolo visual é pequeno por estética, a área clicável
                   não pode ser. O ativo vira cápsula em vez de só mudar de cor —
                   forma sobrevive ao daltonismo, cor não. */
                className="h-6 px-0.5 flex items-center group"
              >
                <span className={`block h-2 rounded-full transition-all duration-300 ${
                  ativo ? "w-6 bg-brand-600" : "w-2 bg-slate-300 group-hover:bg-slate-400"}`} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // per_page no servidor: antes baixava o catálogo inteiro só para cortar com slice().
    api.get("/products", { params: { featured: true, per_page: 8 } }).then((r) => setFeatured(rows(r))).catch(() => {});
    // Campanhas do topo. Falha em silêncio e some: banner é conteúdo de apoio, e
    // a home tem que abrir mesmo que a chamada das artes não responda.
    api.get("/banners").then((r) => setBanners(rows(r))).catch(() => {});
    api.get("/categories").then((r) => setCategories(rows(r))).catch(() => {});
    api.get("/settings").then((r) => setWhatsapp(r.data.whatsapp)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Informe um e-mail válido"); return; }
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Inscrição confirmada! Você receberá nossas ofertas.");
      setEmail("");
    } catch { toast.error("Não foi possível inscrever agora. Tente novamente."); }
    finally { setSubscribing(false); }
  };

  return (
    <div>
      {/* ---------- CAMPANHAS: a vitrine que passa sozinha ----------
          Primeira coisa depois do header, em toda a largura. Sem banner
          cadastrado o bloco inteiro some e a home abre pela seção informativa,
          como abria antes — a loja nunca fica com um buraco no topo. */}
      <HeroSlider banners={banners} />

      {/* ---------- INFORMATIVO: os três caminhos ---------- */}
      {/* Sem foto de banco de imagem: a que estava aqui era a mesma da tela de
          login, e não dizia nada sobre o negócio. O que diferencia a
          Ortopédica é ter três saídas para o mesmo problema. */}
      <section className="relative overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #0B2038 0%, #0F2E52 60%, #15335C 100%)" }}>
        {/* Coluna vertebral da marca, em escala grande e discreta: é o símbolo
            da Ortopédica, não um enfeite geométrico qualquer. */}
        <div aria-hidden className="pointer-events-none absolute -right-24 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-5 opacity-[0.13]">
          {[26, 23, 20, 17, 14, 11, 8].map((d, i) => (
            <span key={i} style={{ width: d * 4, height: d * 4 }} className="rounded-full bg-teal-400" />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-5">
            <Scale value={6} max={6} showValue={false} label="Ortopédica São Paulo" className="opacity-90" />
            <p className="text-eyebrow uppercase tracking-[0.2em] text-teal-300">Cuidar · Reabilitar · Transformar</p>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl">
            Nem todo mundo<br />precisa <span className="text-teal-300">comprar</span>.
          </h1>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-2xl">
            Cadeira de rodas por dois meses de recuperação? Alugue. Palmilha que não
            existe pronta? A gente faz. E se for para comprar, o catálogo está aqui.
          </p>

          {/* Três caminhos com pesos diferentes: comprar é o fluxo completo e
              ocupa o dobro; os outros dois começam no atendimento. */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {WAYS.map((w) => {
              const inner = (
                <>
                  <p className={`text-eyebrow uppercase tracking-wider ${w.primary ? "text-brand-600" : "text-teal-300"}`}>{w.label}</p>
                  <p className={`font-heading text-xl font-semibold mt-1.5 ${w.primary ? "text-slate-900" : "text-white"}`}>{w.lead}</p>
                  <p className={`text-sm mt-2 leading-relaxed ${w.primary ? "text-slate-600" : "text-slate-300"}`}>{w.desc}</p>
                  <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${w.primary ? "text-brand-600" : "text-teal-300"}`}>
                    {w.cta} <ArrowRight size={15} aria-hidden />
                  </span>
                </>
              );
              const cls = `flex flex-col rounded-2xl p-5 transition-colors ${
                w.primary
                  ? "lg:col-span-2 bg-white hover:bg-brand-50/60"
                  : "bg-white/[0.06] border border-white/15 hover:bg-white/[0.11]"}`;
              return w.to ? (
                <Link key={w.key} to={w.to} className={cls} data-testid={`hero-way-${w.key}`}>{inner}</Link>
              ) : (
                <a key={w.key} href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(w.wa)}`}
                  target="_blank" rel="noreferrer" className={cls} data-testid={`hero-way-${w.key}`}>{inner}</a>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONFIANÇA — faixa única.
          Eram 6 cartões com descrição em 11px: ninguém lia, e a faixa competia
          com o catálogo logo abaixo. Como é informação de apoio, virou uma
          linha só, com o texto num tamanho que dá para ler. */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-7 gap-y-2.5">
          {TRUST.map((t) => (
            <span key={t.title} className="inline-flex items-center gap-2 text-sm text-slate-600" title={t.desc}>
              <t.icon size={16} className="text-brand-600 shrink-0" aria-hidden />
              {t.title}
            </span>
          ))}
        </div>
      </section>

      {/* CATEGORIAS */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-slate-900 mb-8">Compre por categoria</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => (
            <Link key={c.slug} to={`/produtos?categoria=${c.slug}`} data-testid={`category-card-${c.slug}`}
              className="group border border-slate-200 rounded-xl overflow-hidden bg-white hover:border-brand-500 hover:shadow-md transition-all text-center">
              {/* `imgUrl` NÃO é enfeite: foto enviada pelo admin é gravada como
                  `/api/files/...`, e sem o prefixo o navegador resolve esse
                  caminho contra o domínio da LOJA, não o da API — 404, e o
                  cartão mostrava o texto alternativo no lugar da foto. Só as
                  categorias com URL absoluta (as semeadas) apareciam. */}
              <div className="overflow-hidden">
                <Thumb src={c.image} icon={Tag} iconSize={22}
                  className="w-full h-24 object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <p className="text-sm font-medium text-slate-800 py-3 px-2">{c.name}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* DESTAQUES */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        {/* Sem destaque cadastrado a seção inteira some — um título sobre um
            vazio faz a loja parecer quebrada. */}
        {featured.length > 0 && <FeaturedSlider products={featured} />}
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="rounded-2xl text-white px-8 py-10 lg:px-14 flex flex-col lg:flex-row items-center gap-8 justify-between" style={{ background: "linear-gradient(120deg, #1565C0 0%, #1976D2 60%, #2283D9 100%)" }}>
          <div>
            <h2 className="font-heading text-2xl font-semibold">Receba ofertas e dicas de reabilitação</h2>
            <p className="text-white/80 text-sm mt-1">Sem spam — só conteúdo útil e promoções exclusivas.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md bg-white rounded-full p-1.5">
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" aria-label="Seu e-mail para a newsletter" placeholder="Seu melhor e-mail"
              data-testid="newsletter-input" className="flex-1 px-4 text-sm text-slate-900 outline-none bg-transparent" />
            <button type="submit" disabled={subscribing} data-testid="newsletter-submit" className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-2 transition-colors">
              {subscribing ? "Enviando" : "Inscrever"} <Send size={14} />
            </button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-slate-900 mb-8 text-center">Perguntas frequentes</h2>
        <div className="space-y-3" data-testid="faq-list">
          {FAQ.map((f) => <FaqItem key={f.q} {...f} />)}
        </div>
      </section>
    </div>
  );
}
